import { useLoadScript } from '@react-google-maps/api';
import CreateProjectDropdown from 'components/DropdownStyle/CreateProjectDropdown';
import ReactSelectDropdown from 'components/ReactSelectDropdown';
import { filterOption } from 'components/ReactSelectDropdown/ReactSelectDropdown';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import BackButtonComponent from '../../../components/BackButtonComponent/BackButtonComponent';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import MapsAutocompleteDropdown from '../../../components/MapsAutocompleteDropdown/MapsAutocompleteDropdown';
import { createNotification } from '../../../modules/notificationManager';
import { PrimaryButton, RadiusButton } from '../../../styledComponents/buttons';
import {
  ColoredText,
  CommonFlex,
  CommonGrid,
  CommonImage,
  CommonText,
  Container,
  ContentHeader,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import { checkHtml, checkUrl } from '../../../utils/helper';
import { InfoSection, ZoneButton } from '../officesStyles';
import ItemsRelatedToCampsSection from './ItemsRelatedToCampsSection';
import ZoneValues from './ZoneValues';

const radiusOptions = [50, 100, 150, 200, 300, 'custom'];
const zoneArray = ['Mina', 'Arafat', 'Muzdalifah'];
const mapLibraries = ['places'];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const CreateUpdateOffice = ({
  officeDetails,
  createOffice,
  updateOffice,
  addOrUpdateOfficeLoading,
  officeCreateUpdateSuccess,
  clearOfficeDetails,
  getPublicZoneList,
  getItemsList,
  itemsList,
  history,
  getOfficeDetails,
  isDetailsLoading,
}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const officeId = queryParams.get('id');
  const { isLoaded, loadError } = useLoadScript({
    id: 'offices-map-script-loader',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });
  const { t } = useTranslation();
  useEffect(() => {
    getItemsList();
  }, []);

  const [officeName, setOfficeName] = useState('');
  const [activeZone, setActiveZone] = useState(null);
  const [nationality, setNationality] = useState('');
  const [zoneValues, setZoneValues] = useState(initialZoneValues);

  const [numberOfPilgrims, setNumberOfPilgrims] = useState(0);

  const [saveErrors, setSaveErrors] = useState({});

  const [action, setAction] = useState('add');
  const handleActiveZoneChange = zone => {
    setActiveZone(zone);
  };

  const clearStateAndProps = () => {
    clearOfficeDetails();
    setOfficeName('');
    setAction('add');
    setActiveZone(null);
    // setAddressMarker(null);
  };
  useEffect(() => {
    if (officeId && location.pathname.includes('update-office')) {
      setAction('update');
    }
  }, [officeId, location.pathname]);

  useEffect(() => {
    if (officeId) {
      getOfficeDetails({ officeId });
    }
  }, [officeId]);
  useEffect(() => {
    getPublicZoneList();
    return () => {
      clearStateAndProps();
    };
  }, []);

  useEffect(() => {
    if (officeCreateUpdateSuccess) {
      clearStateAndProps();
      history.push('/user/offices');
    }
  }, [officeCreateUpdateSuccess]);

  useEffect(() => {
    if (officeDetails && officeDetails.id) {
      setAction('update');
      setOfficeName(officeDetails.name);
      setNationality(officeDetails.nationality || '');
      setNumberOfPilgrims(officeDetails.pilgrims_number || 0);
      const updatedZoneValues = {
        ...initialZoneValues,
      };
      officeDetails.public_zone_detail.forEach(zone => {
        if (zoneArray.includes(zone.public_zone_name)) {
          updatedZoneValues[zone.public_zone_name] = {
            ...updatedZoneValues[zone.public_zone_name],
            ...zone,
            items:
              zone.items.map(item => ({
                id: item.item_id,
                name: item.item_name,
                quantity: item.quantity,
                selected: true,
              })) || [],
            camp_numbers: zone.camp_numbers.map(camp => camp.number) || [],
            permits_and_contracts: zone.permits_and_contracts || [],
          };
        }
      });
      setZoneValues(updatedZoneValues);
      setActiveZone('Mina');
    }
    !officeId && setActiveZone('Mina');
  }, [officeDetails]);

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;

    if (checkUrl(officeName)) {
      formIsValid = false;
      saveErrors['name'] = t('office_name_cannot_contain_url');
    } else if (checkHtml(officeName)) {
      formIsValid = false;
      saveErrors['name'] = t('invalid_office_name');
    }
    if (!nationality) {
      formIsValid = false;
      saveErrors['nationality'] = t('nationality_cannot_be_empty');
    }

    Object.keys(zoneValues).forEach(zone => {
      const zoneData = zoneValues[zone];
      if (!zoneData.address && zone !== 'Muzdalifah') {
        formIsValid = false;
        saveErrors[`${zone}_address`] = t('address_cannot_be_empty');
        createNotification(
          'error',
          `${zone} - ${t('address_cannot_be_empty')}`,
        );
      }
      if (!zoneData.radius && zone !== 'Muzdalifah') {
        formIsValid = false;
        saveErrors[`${zone}_radius`] = t('radius_cannot_be_empty');
        createNotification('error', `${zone} - ${t('radius_cannot_be_empty')}`);
      }
      if (zone !== 'Muzdalifah') {
        if (zoneData.main_gate_number < 0) {
          formIsValid = false;
          saveErrors[`${zone}_main_gate_number`] = t(
            'main_gate_number_cannot_be_negative',
          );
          createNotification(
            'error',
            `${zone} - ${t('main_gate_number_cannot_be_negative')}`,
          );
        }
        if (zoneData.kitchen_number < 0) {
          formIsValid = false;
          saveErrors[`${zone}_kitchen_number`] = t(
            'kitchen_number_cannot_be_negative',
          );
          createNotification(
            'error',
            `${zone} - ${t('kitchen_number_cannot_be_negative')}`,
          );
        }
        if (zoneData.bathroom_number < 0) {
          formIsValid = false;
          saveErrors[`${zone}_bathroom_number`] = t(
            'bathroom_number_cannot_be_negative',
          );
          createNotification(
            'error',
            `${zone} - ${t('bathroom_number_cannot_be_negative')}`,
          );
        }
      }
      if ((!zoneData.area || zoneData.area === 0) && zone !== 'Muzdalifah') {
        formIsValid = false;
        saveErrors[`${zone}_camp_area`] = t(
          'camp_area_cannot_be_empty_or_zero',
        );
        createNotification(
          'error',
          `${zone} - ${t('camp_area_cannot_be_empty_or_zero')}`,
        );
      }
    });

    setSaveErrors(saveErrors);
    return formIsValid;
  };

  const handleAddOrUpdateOffice = () => {
    if (checkError()) {
      const payload = {
        name: officeName,
        nationality: nationality,
        pilgrims_number: numberOfPilgrims,
        public_zone_detail: Object.values(zoneValues)
          .filter(z => z.latitude && z.longitude)
          .map(zone => ({
            ...zone,
            items: zone.items.map(item => ({
              id: item.item_id ? item.item_id : item.id,
              item_name: item.name,
              quantity: item.quantity,
            })),
            camp_numbers: zone.camp_numbers
              ?.filter(camp => camp)
              ?.map(camp => ({ number: camp })),
            permits_and_contracts: zone.permits_and_contracts.map(contract => {
              const n = {
                pdf_file: contract.file ? contract.file : contract.preview,
                name: contract.name,
                description: contract.description,
              };
              if (contract.id) {
                n.id = contract.id;
              }
              return n;
            }),
          })),
      };
      payload.public_zone_detail = payload.public_zone_detail.filter(
        zone => zone.public_zone_name,
      );
      payload.public_zone_detail = payload.public_zone_detail.map(zone => {
        if (typeof zone.sketch_image === 'string') {
          delete zone.sketch_image;
        } else if (!zone.sketch_image && action === 'add') {
          delete zone.sketch_image;
        } else zone.sketch_image = zone.sketch_image?.file;
        if (!zone.camp_numbers) {
          delete zone.camp_numbers;
        }
        if (zone.public_zone_name === 'Muzdalifah') {
          delete zone.bathroom_number;
          delete zone.kitchen_number;
          delete zone.main_gate_number;
          delete zone.camp_numbers;
        }
        return zone;
      });
      console.log('🚀 ~ handleAddOrUpdateOffice ~ payload:', payload);
      if (action === 'update' && officeDetails && officeDetails.id) {
        payload.office_id = officeDetails.id;
        updateOffice(payload);
      } else {
        createOffice(payload);
      }
    }
  };

  const onOfficeNameChange = e => {
    let errors = { ...saveErrors };
    delete errors['name'];
    setSaveErrors(errors);
    setOfficeName(e.target.value);
  };

  const onNationalityChange = e => {
    let errors = { ...saveErrors };
    delete errors['nationality'];
    setSaveErrors(errors);
    setNationality(e.target.value);
  };

  const onNumberOfPilgrimsChange = e => {
    let errors = { ...saveErrors };
    delete errors['numberOfPilgrims'];
    setSaveErrors(errors);
    const value = parseInt(e.target.value, 10) || 0; // Convert to number or default to 0

    setNumberOfPilgrims(value);
  };
  return (
    <div className="content">
      <ContentHeader>
        <BackButtonComponent
          onClick={() => history.push('/user/offices')}
          subTitle={t('back_to_all_offices')}
        />
      </ContentHeader>
      {isDetailsLoading && action === 'update' ? (
        <ComponentCircleLoader />
      ) : (
        <Container padding="0">
          <InfoSection>
            <CommonText name title fontSize="16px">
              {action === 'update'
                ? t('update_office_details')
                : t('add_office_details')}
            </CommonText>
            <div style={{ width: '30%' }}>
              <InputWithLabelSection margin="20px 0 0 0">
                <InputLabel>
                  {t('office_name')}
                  <RedStar>*</RedStar>
                </InputLabel>
                <InputField
                  type="text"
                  placeholder={t('type_office_name')}
                  value={officeName}
                  onChange={e => onOfficeNameChange(e)}
                  error={
                    saveErrors &&
                    saveErrors['name'] &&
                    saveErrors['name'] !== undefined
                  }
                />
                {saveErrors &&
                  saveErrors['name'] &&
                  saveErrors['name'] !== undefined && (
                    <FormAlert margin="0 0 0 5px">
                      {saveErrors['name']}
                    </FormAlert>
                  )}
              </InputWithLabelSection>
              <InputWithLabelSection margin="20px 0 0 0">
                <InputLabel>
                  {t('nationality')}
                  <RedStar>*</RedStar>
                </InputLabel>
                <InputField
                  type="text"
                  placeholder={t('type_nationality')}
                  value={nationality}
                  onChange={e => onNationalityChange(e)}
                  error={
                    saveErrors &&
                    saveErrors['nationality'] &&
                    saveErrors['nationality'] !== undefined
                  }
                />
                {saveErrors &&
                  saveErrors['nationality'] &&
                  saveErrors['nationality'] !== undefined && (
                    <FormAlert margin="0 0 0 5px">
                      {saveErrors['nationality']}
                    </FormAlert>
                  )}
              </InputWithLabelSection>
              <InputWithLabelSection margin="20px 0 0 0">
                <InputLabel>
                  {t('number_of_pilgrims')}
                  <RedStar>*</RedStar>
                </InputLabel>
                <InputField
                  type="number"
                  placeholder={t('type_number_of_pilgrims')}
                  value={numberOfPilgrims}
                  onChange={e => onNumberOfPilgrimsChange(e)}
                  error={
                    saveErrors &&
                    saveErrors['numberOfPilgrims'] &&
                    saveErrors['numberOfPilgrims'] !== undefined
                  }
                />
                {saveErrors &&
                  saveErrors['numberOfPilgrims'] &&
                  saveErrors['numberOfPilgrims'] !== undefined && (
                    <FormAlert margin="0 0 0 5px">
                      {saveErrors['numberOfPilgrims']}
                    </FormAlert>
                  )}
              </InputWithLabelSection>
            </div>

            <CommonFlex
              style={{
                marginTop: '30px',
                zIndex: 2,
                backgroundColor: '#F1F1F1',
                width: 'max-content',
                padding: '10px',
                borderRadius: '6px',
              }}
            >
              {zoneArray.map(zone => (
                <ZoneButton
                  active={activeZone === zone}
                  onClick={() => handleActiveZoneChange(zone)}
                >
                  {zone}
                </ZoneButton>
              ))}
            </CommonFlex>
            {activeZone && (
              <ZoneValues
                {...{
                  saveErrors,
                  loadError,
                  isLoaded,
                  radiusOptions,
                  zoneValues,
                  setZoneValues,
                  activeZone,
                  itemsList,
                  numberOfPilgrims,
                  setSaveErrors,
                }}
              />
            )}

            <PrimaryButton
              width="100%"
              margin="20px 0 0"
              disabled={!officeName.trim() || !nationality.trim()}
              onClick={() => handleAddOrUpdateOffice()}
            >
              {addOrUpdateOfficeLoading ? (
                <ButtonTextLoader
                  fontSize="14px"
                  loadingText={
                    action === 'update'
                      ? t('update_office')
                      : t('create_office')
                  }
                />
              ) : action === 'update' ? (
                t('update_office')
              ) : (
                t('create_office')
              )}
            </PrimaryButton>
          </InfoSection>
        </Container>
      )}
    </div>
  );
};

export default CreateUpdateOffice;

const initialZoneValues = {
  Mina: {
    area: 0,
    address: '',
    bathroom_number: 0,
    camp_numbers: [],
    items: [],
    kitchen_number: 0,
    latitude: '',
    longitude: '',
    main_gate_number: 0,
    permits_and_contracts: [],
    public_zone_name: 'Mina',
    radius: 200,
    sketch_image: null,
  },
  Arafat: {
    area: 0,
    address: '',
    bathroom_number: 0,
    camp_numbers: [],
    items: [],
    kitchen_number: 0,
    latitude: '',
    longitude: '',
    main_gate_number: 0,
    permits_and_contracts: [],
    public_zone_name: 'Arafat',
    radius: 200,
    sketch_image: null,
  },
  Muzdalifah: {
    area: 0,
    address: '',
    items: [],
    latitude: '',
    longitude: '',
    permits_and_contracts: [],
    public_zone_name: 'Muzdalifah',
    radius: 200,
    sketch_image: null,
  },
};
